import React, { useEffect } from 'react';
import { Container } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
    Block,
    palette,
    StatusBadge,
    TextDefault,
    ThemedButton,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        font-size: 10px;
        font-weight: 600;
        color: ${palette.graphite.charcoal};
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 9px 12px;
        border-bottom: 1px solid ${palette.graphite.silver};
    }
    td {
        padding: 12px;
        border-bottom: 1px solid ${palette.graphite.cloud};
        color: ${palette.graphite.heavyMetal};
        vertical-align: middle;
    }
    tr {
        cursor: pointer;
    }
    tr:hover td {
        background: ${palette.graphite.cloud};
    }
`;

function AwarePhishCampaigns() {
    const { store, awarePhishStore } = useStores();
    const navigate = useNavigate();
    const { currentCompany } = store;
    const { campaigns, loadingCampaigns } = awarePhishStore;

    useEffect(() => {
        if (currentCompany && currentCompany.company_id) {
            awarePhishStore.loadCampaigns(currentCompany.company_id);
        }
    }, [currentCompany && currentCompany.company_id]);

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwarePHISH simulations</title>
            </Helmet>
            <HeaderSection
                heading={'Simulations'}
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'List of phishing simulations for this company.'
                            }
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <ThemedButton
                        text="+ New simulation"
                        mode={'primary'}
                        onClick={() =>
                            navigate('/aware-phish/campaigns/new')
                        }
                        $noWrap
                    />
                }
            />
            <AwarePhishSubNav active="campaigns" />

            <Block tablePadding column>
                {loadingCampaigns ? (
                    <TextDefault lighter>Loading…</TextDefault>
                ) : !campaigns.length ? (
                    <TextDefault lighter>
                        No simulations yet. Create one to get started.
                    </TextDefault>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Launch</th>
                                <th>Sent</th>
                                <th>Clicked</th>
                                <th>Submitted</th>
                                <th>Reported</th>
                            </tr>
                        </thead>
                        <tbody>
                            {campaigns.map((c) => (
                                <tr
                                    key={c.id}
                                    onClick={() =>
                                        navigate(
                                            `/aware-phish/campaigns/${c.id}`
                                        )
                                    }
                                >
                                    <td>{c.name}</td>
                                    <td>
                                        <StatusBadge
                                            text={c.status || '—'}
                                            color={palette.graphite.charcoal}
                                        />
                                    </td>
                                    <td>
                                        {c.launchAt
                                            ? new Date(
                                                  c.launchAt
                                              ).toLocaleString()
                                            : '—'}
                                    </td>
                                    <td>{c.sent}</td>
                                    <td>{c.clicked}</td>
                                    <td>{c.submitted}</td>
                                    <td>{c.reported}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Block>
        </Container>
    );
}

export default observer(AwarePhishCampaigns);
